import React, {
  useEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { debounce } from '../../services/utilityFunctions';

/**
 * This is a new updated component to replace the still used in a lot of places:
 * /react/commonComponents/lists/List.jsx
 *
 *  QUERY in this component we are using the forward ref to set query
 *  and or destroy table
 *  see example ReportAcademyScorm.jsx
 *  scormReportHelper.jsx
 *
 */
const ServerSideList = forwardRef((props, ref) => {
  const {
    id,
    dataSourceURL,
    dataSourceHeaders,
    dataSourceParams,
    dataTransform,
    dataRequestTransform,
    columns,
    onInit,
    onRowClick,
    onDataFetch,
    defaultPageSize,
    detail,
    query,
    sortable,
    onReload,
  } = props;

  const handleListInitialization = detailParam => {
    let dataConfig = {
      type: 'local',
      source: [],
    };

    if (dataSourceURL.length > 0) {
      dataConfig = {
        type: 'remote',
        source: {
          read: {
            url: dataSourceURL,
            method: 'GET',
            headers: dataSourceHeaders,
            map: raw => {
              if (onDataFetch) {
                onDataFetch(raw);
              }
              if (dataRequestTransform) {
                return dataRequestTransform(raw);
              }
              let data = raw;
              if (raw.data) {
                data = dataTransform ? raw.data.map(dataTransform) : raw.data;
              }
              return data;
            },
            params: { ...(dataSourceParams || {}), ...(query || {}) },
          },
        },
        pageSize: defaultPageSize,
        serverPaging: true,
        serverFiltering: true,
        serverSorting: true,
        saveState: {
          cookie: false,
          webstorage: false,
        },
        search: {
          delay: 300,
        },
      };
    }

    const config = {
      data: dataConfig,

      layout: {
        theme: 'default',
        class: '',
        scroll: false,
        footer: false,
      },

      sortable,

      pagination: true,
      pagingType: 'full_numbers',
      columns,
      detail: detailParam || detail,
      rows: onRowClick
        ? {
            beforeTemplate: (row, data) =>
              row.addClass('clickable-row').on('click', () => onRowClick(data)),
          }
        : undefined,
    };
    const tableElement = $(`#${id}`);
    const table = tableElement.mDatatable(config);
    if (onInit) {
      table.on('m-datatable--on-init', onInit);
    }

    if (onReload) {
      table.on('m-datatable--on-reloaded', onReload);
    }
    return table;
  };

  const updateQueryParams = (queryParam, table = undefined) => {
    const tableEl = table || $(`#${id}`).mDatatable();
    if (tableEl) {
      const newQuery = { ...tableEl.getDataSourceQuery(), ...queryParam };
      tableEl.setDataSourceQuery(newQuery);
      const newPagination = {
        ...tableEl.getDataSourceParam('pagination'),
        page: 1,
      };
      tableEl.setDataSourceParam('pagination', newPagination);
      tableEl.reload();
    }
  };

  const updateList = (queryParam, detailParam) => {
    const table = $(`#${id}`).mDatatable();
    if (table) {
      table.destroy();
    }
    const tableEl = handleListInitialization(detailParam);
    debounce(updateQueryParams(queryParam, tableEl), 300);
  };

  // INIT TABLE
  useEffect(() => {
    handleListInitialization();
  }, []);

  const isMountedRef = useRef(false);

  useEffect(() => {
    // This should run only after first change in filters, so we don't do
    // 2 similar requests, like that the table only renders once
    // with correct data sent on params: { ...dataSourceParams || {}, ...query || {} },
    // in function handleListInitialization()
    if (isMountedRef.current) {
      const tableEl = $(`#${id}`).mDatatable();
      if (tableEl) {
        const newQuery = { ...tableEl.getDataSourceQuery(), ...query };
        tableEl.setDataSourceQuery(newQuery);
        const newPagination = {
          ...tableEl.getDataSourceParam('pagination'),
          page: 1,
        };
        tableEl.setDataSourceParam('pagination', newPagination);
        tableEl.reload();
      }
    } else {
      isMountedRef.current = true;
    }
  }, [query]);

  /** OUTSIDE FUNCTIONS */
  useImperativeHandle(ref, () => ({
    destroy() {
      const table = $(`#${id}`).mDatatable();
      table.destroy();
    },
    mountTable() {
      handleListInitialization();
    },
    updateQuery(queryParam) {
      updateQueryParams(queryParam);
    },
    resetTable(queryParam, detailParam) {
      updateList(queryParam, detailParam);
    },
    forceReload() {
      const table = $(`#${id}`).mDatatable();
      table.reload();
    },
  }));

  return <div className="m_datatable" id={id} />;
});

ServerSideList.propTypes = {
  /** DataTable div container id */
  id: PropTypes.string.isRequired,
  /** Server endpoint for table data */
  dataSourceURL: PropTypes.string.isRequired,
  /** Data source constant headers */
  dataSourceHeaders: PropTypes.shape(),
  /** Data source constant params, example for populate! */
  dataSourceParams: PropTypes.shape(),
  /** Query configuration object, where we define the filters for the lists */
  query: PropTypes.shape().isRequired,
  dataTransform: PropTypes.func,
  dataRequestTransform: PropTypes.func,
  /** Column definitions, passed directly to jQuery component, see it's docs */
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      textAlign: PropTypes.string,
      width: PropTypes.number,
      sortable: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
      overflow: PropTypes.string,
      template: PropTypes.func,
    }),
  ).isRequired,
  onInit: PropTypes.func,
  onReload: PropTypes.func,
  onRowClick: PropTypes.func,
  onDataFetch: PropTypes.func,
  defaultPageSize: PropTypes.number,
  detail: PropTypes.shape({
    title: PropTypes.string,
    content: PropTypes.func,
  }),
  sortable: PropTypes.bool,
};

ServerSideList.defaultProps = {
  dataSourceHeaders: null,
  dataSourceParams: null,
  dataTransform: null,
  dataRequestTransform: null,
  onInit: null,
  onRowClick: null,
  onDataFetch: null,
  defaultPageSize: 20,
  detail: null,
  sortable: true,
  onReload: null,
};

export default ServerSideList;
